import {
  collection,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export const MAX_QUIZ_TRIES_BEFORE_SKIP = 2;

export function lectureQuizId(dayId) {
  return `lecture_${dayId}`;
}

export function moduleQuizId(moduleId) {
  return `module_${moduleId}`;
}

export function erQuizId(erId) {
  return `er_${erId}`;
}

export function imrQuizId(imrId) {
  return `imr_${imrId}`;
}

function quizRef(userId, quizId) {
  return doc(db, 'userQuizzes', userId, 'items', quizId);
}

export async function loadQuiz(userId, quizId) {
  if (!userId || !quizId) return null;
  const snap = await getDoc(quizRef(userId, quizId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveQuizQuestions(userId, quizId, payload) {
  if (!userId || !quizId || !payload?.questions?.length) return;
  await setDoc(
    quizRef(userId, quizId),
    {
      kind: payload.kind || 'lecture',
      title: payload.title || '',
      subject: payload.subject || '',
      moduleId: payload.moduleId || '',
      lectureId: payload.lectureId || '',
      questions: payload.questions,
      questionCount: payload.questions.length,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function recordQuizAttempt(userId, quizId, { score, total, passed }) {
  if (!userId || !quizId) return null;
  const ref = quizRef(userId, quizId);
  const snap = await getDoc(ref);
  const prev = snap.exists() ? snap.data() : {};
  const attempts = (Number(prev.attempts) || 0) + 1;
  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  const history = Array.isArray(prev.history) ? [...prev.history] : [];
  history.push({
    score,
    total,
    pct,
    passed: Boolean(passed),
    at: Date.now(),
  });
  const next = {
    attempts,
    lastScore: score,
    lastTotal: total,
    lastPct: pct,
    passed: Boolean(passed) || Boolean(prev.passed),
    cleared: Boolean(passed) || Boolean(prev.cleared),
    history: history.slice(-20),
    updatedAt: serverTimestamp(),
  };
  if (passed) next.passedAt = serverTimestamp();
  await setDoc(ref, next, { merge: true });
  return { ...prev, ...next, id: quizId };
}

export async function recordExtendedResponseAttempt(userId, quizId, payload) {
  if (!userId || !quizId) return null;
  const ref = quizRef(userId, quizId);
  const snap = await getDoc(ref);
  const prev = snap.exists() ? snap.data() : {};
  const attempts = (Number(prev.attempts) || 0) + 1;
  const total = Number(payload.total) || 0;
  const pct = Math.round((total / 6) * 100);
  const passed = Boolean(payload.passed);
  const history = Array.isArray(prev.history) ? [...prev.history] : [];
  history.push({
    score: total,
    total: 6,
    pct,
    passed,
    trait1: payload.trait1,
    trait2: payload.trait2,
    trait3: payload.trait3,
    wordCount: payload.wordCount,
    at: Date.now(),
  });
  const next = {
    kind: 'extended',
    title: payload.title || prev.title || '',
    subject: payload.subject || 'language-arts',
    extendedResponseId: payload.extendedResponseId || '',
    attempts,
    lastScore: total,
    lastTotal: 6,
    lastPct: pct,
    lastStudentText: payload.studentText || '',
    lastWordCount: payload.wordCount || 0,
    lastTrait1: payload.trait1,
    lastTrait2: payload.trait2,
    lastTrait3: payload.trait3,
    lastFeedbackTrait1: payload.feedbackTrait1 || '',
    lastFeedbackTrait2: payload.feedbackTrait2 || '',
    lastFeedbackTrait3: payload.feedbackTrait3 || '',
    passed: passed || Boolean(prev.passed),
    cleared: passed || Boolean(prev.cleared),
    history: history.slice(-20),
    updatedAt: serverTimestamp(),
  };
  if (passed) next.passedAt = serverTimestamp();
  await setDoc(ref, next, { merge: true });
  return { ...prev, ...next, id: quizId };
}

export async function markQuizCleared(userId, quizId) {
  if (!userId || !quizId) return;
  await setDoc(
    quizRef(userId, quizId),
    { cleared: true, skippedAfterFails: true, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export function subscribeStudentQuizzes(userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  return onSnapshot(
    collection(db, 'userQuizzes', userId, 'items'),
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    () => callback([])
  );
}

export function buildQuizAiReport(items = []) {
  const lecture = items.filter((q) => q.kind === 'lecture' && (q.attempts || 0) > 0);
  const moduleQ = items.filter((q) => q.kind === 'module' && (q.attempts || 0) > 0);
  const erQ = items.filter((q) => q.kind === 'extended' && (q.attempts || 0) > 0);
  const imrQ = items.filter((q) => q.kind === 'image-map' && (q.attempts || 0) > 0);
  const failedModules = moduleQ.filter((q) => !q.passed);
  const passedModules = moduleQ.filter((q) => q.passed);
  const avgModule =
    moduleQ.length > 0
      ? Math.round(moduleQ.reduce((s, q) => s + (Number(q.lastPct) || 0), 0) / moduleQ.length)
      : null;
  const avgLecture =
    lecture.length > 0
      ? Math.round(lecture.reduce((s, q) => s + (Number(q.lastPct) || 0), 0) / lecture.length)
      : null;

  const lines = [];
  if (!lecture.length && !moduleQ.length && !erQ.length && !imrQ.length) {
    lines.push('No lecture, module, or extended-response work has been taken yet.');
  } else {
    lines.push(
      `Lecture quizzes: ${lecture.filter((q) => q.passed).length} passed of ${lecture.length} taken${avgLecture != null ? ` (avg ${avgLecture}%)` : ''}.`
    );
    lines.push(
      `Module quizzes: ${passedModules.length} passed of ${moduleQ.length} taken${avgModule != null ? ` (avg ${avgModule}%)` : ''}.`
    );
    if (failedModules.length) {
      lines.push(
        `Needs review: ${failedModules.map((q) => `${q.title || 'Module'} (${q.lastPct || 0}% after ${q.attempts} ${q.attempts === 1 ? 'try' : 'tries'})`).join('; ')}.`
      );
    } else if (moduleQ.length) {
      lines.push('Module quiz performance is on track — continue to the next module after each pass.');
    }
    if (erQ.length) {
      const passedEr = erQ.filter((q) => q.passed).length;
      lines.push(
        `Language Arts extended responses: ${passedEr} passed of ${erQ.length} taken (GED 3-trait scores out of 6).`
      );
    }
    if (imrQ.length) {
      const passedImr = imrQ.filter((q) => q.passed).length;
      lines.push(
        `Social Studies image and map readings: ${passedImr} passed of ${imrQ.length} taken (admin-authored MCQs).`
      );
    }
  }
  return {
    lectureTaken: lecture.length,
    lecturePassed: lecture.filter((q) => q.passed).length,
    moduleTaken: moduleQ.length,
    modulePassed: passedModules.length,
    avgLecture,
    avgModule,
    failedModules,
    narrative: lines.join(' '),
    lines,
  };
}

export function summarizeQuizzes(items = []) {
  const lecture = items.filter((q) => q.kind === 'lecture');
  const moduleQ = items.filter((q) => q.kind === 'module');
  const erQ = items.filter((q) => q.kind === 'extended');
  const imrQ = items.filter((q) => q.kind === 'image-map');
  const taken = items.filter((q) => (q.attempts || 0) > 0);
  const passed = taken.filter((q) => q.passed);
  const avg =
    taken.length > 0
      ? Math.round(taken.reduce((s, q) => s + (Number(q.lastPct) || 0), 0) / taken.length)
      : null;
  return {
    lectureTaken: lecture.filter((q) => (q.attempts || 0) > 0).length,
    lecturePassed: lecture.filter((q) => q.passed).length,
    moduleTaken: moduleQ.filter((q) => (q.attempts || 0) > 0).length,
    modulePassed: moduleQ.filter((q) => q.passed).length,
    erTaken: erQ.filter((q) => (q.attempts || 0) > 0).length,
    erPassed: erQ.filter((q) => q.passed).length,
    imrTaken: imrQ.filter((q) => (q.attempts || 0) > 0).length,
    imrPassed: imrQ.filter((q) => q.passed).length,
    avgPct: avg,
    items,
  };
}
